import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { AdjustIcon } from "../Icons";

const TableHeaderControls = ({
  onAdvancedSearchToggle,
  onDeleteProject,
  onAdd,
  onExport,
  searchField = "projectName",
  searchValue,
  onSearch,
  hasSelectedRows,
  placeholderSearch = "Search...",
  titleAddButton,
  titleDeleteButton,
  titleExportButton,
  isDisplayAddButton = true,
  isDisplayDeleteButton = true,
  isDisplayExportButton = false,
  isDisplayAdvancedSearch = true,
  isSearching = false,
  isExportLoading = false,
  userRole,
  searchInputTestId = "table-header-controls-search-input",
  addButtonTestId = "table-header-controls-add-button",
  deleteButtonTestId = "table-header-controls-delete-button",
  advancedSearchButtonTestId = "table-header-controls-advanced-search-button",
  controlsTestId = "table-header-controls",
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const handleSearch = (e) => {
    if (onSearch) {
      // Send search query as an object to be compatible with AdvancedSearch
      onSearch({ [searchField]: e.target.value });
    }
  };

  return (
    <div className="mb-4 flex flex-row items-center justify-between" data-testid={controlsTestId}>
      {/* Search section */}
      <div className="flex flex-row items-center space-x-4 flex-nowrap flex-shrink-0">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className={`h-5 w-5 ${colors.textMuted}`} />
          </div>
          <input
            type="text"
            data-testid={searchInputTestId}
            placeholder={placeholderSearch}
            value={searchValue ?? ""}
            onChange={handleSearch}
            className={`block w-96 border py-2 pr-3 pl-10 h-10 ${colors.borderPrimary} rounded-md leading-5 ${colors.bgSecondary} ${colors.textPrimary} ${colors.borderHoverPrimary} ${colors.textActive} outline-none text-sm`}
          />
          {isSearching && (
            <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
              <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.textActive}`}></div>
            </div>
          )}
        </div>

        {isDisplayAdvancedSearch && (
          <button
            type="button"
            data-testid={advancedSearchButtonTestId}
            onClick={onAdvancedSearchToggle}
            className={`inline-flex items-center justify-center border px-4 py-2 h-10 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
          >
            <AdjustIcon className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">{t("project.advancedSearchButton")}</span>
            <span className="sm:hidden">{t("project.advancedSearchButtonMobile")}</span>
          </button>
        )}
      </div>

      {/* Action buttons section */}
      <div className="flex flex-row items-center space-x-3 flex-shrink-0">
        {isDisplayDeleteButton && (
          <button
            type="button"
            data-testid={deleteButtonTestId}
            onClick={onDeleteProject}
            disabled={!hasSelectedRows}
            className={`inline-flex items-center justify-center rounded-md border px-4 py-2 h-10 text-sm font-medium whitespace-nowrap shadow-sm transition-colors ${colors.focusRing2} focus:ring-offset-2 focus:outline-none overflow-hidden ${hasSelectedRows
              ? ` bg-transparent ${colors.borderDangerOutLineButton} ${colors.textDanger}`
              : `${colors.borderPrimary} ${colors.textSecondary} cursor-not-allowed opacity-50`
              }`}
          >
            <span className="hidden sm:inline truncate" title={titleDeleteButton}>{titleDeleteButton}</span>
            <span className="sm:hidden truncate" title={titleDeleteButton}>{titleDeleteButton}</span>
          </button>
        )}

        {isDisplayExportButton && (
          <button
            onClick={onExport}
            disabled={isExportLoading}
            className={`px-4 py-2 h-10 text-sm font-semibold rounded border ${colors.textActive} transition-colors disabled:opacity-50`}
          >
            <span className="hidden sm:inline truncate flex-1 text-left">
              {titleExportButton}
            </span>
          </button>
        )}

        {isDisplayAddButton && (
          <button
            data-testid={addButtonTestId}
            onClick={onAdd}
            className={`font-600 inline-flex items-center justify-center rounded-md border-transparent px-4 py-2 h-10 text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors overflow-hidden`}
          >
            <PlusIcon className="mr-2 h-4 w-4 sm:hidden flex-shrink-0" />
            <span className="hidden sm:inline truncate" title={titleAddButton}>{titleAddButton}</span>
            <span className="sm:hidden truncate" title={titleAddButton}>{titleAddButton}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default TableHeaderControls;
